const pdfjsUrl = `${process.env.PUBLIC_URL}/pdfjs/pdf.min.js`;
const workerUrl = `${process.env.PUBLIC_URL}/pdfjs/pdf.worker.min.js`;
const rootId = "pdf-print-root";
const styleId = "pdf-print-style";
const printDpi = 200;
const maxSide = 3000;

const libraryMessage =
  "PDF પ્રિન્ટ લાઇબ્રેરી લોડ થઈ નથી, કૃપા કરીને ફરીથી પ્રયત્ન કરો.";
const prepareMessage =
  "પ્રિન્ટ વિન્ડો તૈયાર થઈ શકી નથી, કૃપા કરીને ફરીથી પ્રયત્ન કરો.";

let pdfjsLoader: Promise<any> | null = null;
let pdfjsFailures = 0;
let preparedName = "";
let preparedUrls: string[] = [];
let prepareToken = 0;

const loadPdfjs = (): Promise<any> => {
  if (!pdfjsLoader) {
    // The browser remembers a module that failed to load until the page reloads, so a retry asks for a new URL.
    const url = pdfjsFailures ? `${pdfjsUrl}?retry=${pdfjsFailures}` : pdfjsUrl;

    pdfjsLoader = import(/* webpackIgnore: true */ url)
      .then((pdfjs: any) => {
        pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;

        return pdfjs;
      })
      .catch(() => {
        pdfjsLoader = null;
        pdfjsFailures += 1;

        throw new Error(libraryMessage);
      });
  }

  return pdfjsLoader;
};

const base64ToBytes = (content: string) => {
  const binary = atob(content);
  const bytes = new Uint8Array(binary.length);

  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }

  return bytes;
};

const canvasToUrl = (canvas: HTMLCanvasElement): Promise<string> =>
  new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) {
        resolve(URL.createObjectURL(blob));
      } else {
        reject(new Error(prepareMessage));
      }
    }, "image/png");
  });

const revokeAll = (urls: string[]) => {
  urls.forEach((url) => URL.revokeObjectURL(url));
};

const printCss = (widthMm: number, heightMm: number) => `
#${rootId} { display: none; }
@media print {
  @page { size: ${widthMm}mm ${heightMm}mm; margin: 0; }
  html, body { margin: 0 !important; padding: 0 !important; background: #ffffff !important; height: auto !important; min-height: 0 !important; overflow: visible !important; }
  body > *:not(#${rootId}) { display: none !important; }
  #${rootId} { display: block !important; }
  #${rootId} img { display: block; width: ${widthMm}mm; height: ${heightMm}mm; object-fit: contain; break-after: page; page-break-after: always; }
  #${rootId} img:last-child { break-after: auto; page-break-after: auto; }
}`;

export const clearPrintView = () => {
  prepareToken += 1;

  document.getElementById(rootId)?.remove();
  document.getElementById(styleId)?.remove();

  revokeAll(preparedUrls);

  preparedUrls = [];
  preparedName = "";
};

export const isPrintViewReady = (name: string): boolean =>
  !!name && preparedName === name;

export const preparePrintView = async (name: string, content: string) => {
  clearPrintView();

  const token = prepareToken;
  const pdfjs = await loadPdfjs();
  const urls: string[] = [];
  let loadingTask: any = null;

  try {
    loadingTask = pdfjs.getDocument({ data: base64ToBytes(content) });

    const pdf = await loadingTask.promise;

    let widthMm = 0;
    let heightMm = 0;

    for (let number = 1; number <= pdf.numPages; number += 1) {
      if (token !== prepareToken) break;

      const page = await pdf.getPage(number);
      const base = page.getViewport({ scale: 1 });

      if (number === 1) {
        widthMm = Math.round(((base.width * 25.4) / 72) * 100) / 100;
        heightMm = Math.round(((base.height * 25.4) / 72) * 100) / 100;
      }

      const scale = Math.min(
        printDpi / 72,
        maxSide / Math.max(base.width, base.height)
      );
      const viewport = page.getViewport({ scale });
      const canvas = document.createElement("canvas");

      canvas.width = Math.ceil(viewport.width);
      canvas.height = Math.ceil(viewport.height);

      await page.render({ canvas, viewport }).promise;

      urls.push(await canvasToUrl(canvas));

      canvas.width = 0;
      canvas.height = 0;
      page.cleanup();
    }

    if (token !== prepareToken) {
      revokeAll(urls);
      return;
    }

    const style = document.createElement("style");
    const root = document.createElement("div");

    style.id = styleId;
    style.textContent = printCss(widthMm, heightMm);
    root.id = rootId;

    const images = urls.map((url) => {
      const image = document.createElement("img");

      image.src = url;
      image.alt = "";
      root.appendChild(image);

      return image;
    });

    document.head.appendChild(style);
    document.body.appendChild(root);

    await Promise.all(
      images.map((image) => image.decode().catch(() => undefined))
    );

    if (token !== prepareToken) {
      root.remove();
      style.remove();
      revokeAll(urls);
      return;
    }

    preparedUrls = urls;
    preparedName = name;
  } catch (error) {
    revokeAll(urls);

    throw new Error(prepareMessage);
  } finally {
    loadingTask?.destroy().catch(() => undefined);
  }
};

export const openPrintWindow = (name: string): boolean => {
  if (!isPrintViewReady(name)) return false;

  window.print();

  return true;
};
